import * as React from 'react';
import {Mutation} from 'react-apollo';
// @ts-expect-error
import type {DataProxy} from 'react-apollo';

import type {
    QueuedEmail as QueuedEmailFragment,
    EmailQueuedRetryInput,
    RetryQueuedEmail as RetryQueuedEmailMutation,
    RetryQueuedEmailVariables as RetryQueuedEmailMutationVariables,
    EmailSequenceDeleteInput,
    DeleteEmailSequence as DeleteEmailSequenceMutation,
    DeleteEmailSequenceVariables as DeleteEmailSequenceMutationVariables,
    DelayQueuedEmail as DelayQueuedEmailMutation,
    DelayQueuedEmailVariables as DelayQueuedEmailMutationVariables,
    DelayQueuedEmailInput,
    EmailQueuedSendNowInput,
    SendNowQueuedEmail as SendNowQueuedEmailMutation,
    SendNowQueuedEmailVariables as SendNowQueuedEmailMutationVariables,
} from 'nutshell-graphql-types';

import {EmailRow} from '../email-row';
import DELAY_QUEUED_EMAIL from '../graphql/mutations/delay-queued-email.graphql';
import DELETE_EMAIL_SEQUENCE from '../graphql/mutations/delete-email-sequence.graphql';
import RETRY_QUEUED_EMAIL from '../graphql/mutations/retry-queued-email.graphql';
import SEND_NOW_QUEUED_EMAIL from '../graphql/mutations/send-now-queued-email.graphql';
import {mapEmailQueuedToEmailListItem} from '../utils';

import {getActionMenuOptions, getSequenceInfo} from './queued-email-row-item-utils';

type OwnProps = {
    queuedEmail: QueuedEmailFragment;
    isExpanded?: boolean;
    isFirstOfDate: boolean;
    navToSequenceDetails?: () => void;
    onEmailUpdate?: () => void;
    onEmailRetried?: (
        proxy: DataProxy,
        update: {
            data?: RetryQueuedEmailMutation;
        }
    ) => void;
    onEmailSendNow?: (
        proxy: DataProxy,
        update: {
            data?: SendNowQueuedEmailMutation;
        }
    ) => void;
    onEmailDelayed?: (
        proxy: DataProxy,
        update: {
            data?: DelayQueuedEmailMutation;
        }
    ) => void;
    onEmailEdit?: () => void;
    onSequenceDeleted?: (
        proxy: DataProxy,
        update: {
            data?: DeleteEmailSequenceMutation;
        }
    ) => void;
};

type OnRetryEmailProps = {
    onRetry: (arg1: {
        payload: EmailQueuedRetryInput;
        update: (
            proxy: DataProxy,
            update: {
                data?: RetryQueuedEmailMutation;
            }
        ) => void;
    }) => Promise<any>;
};

type WithRetryQueuedEmail = OwnProps & OnRetryEmailProps;

type OnSendNowEmailProps = {
    onSendNow: (arg1: {
        payload: EmailQueuedSendNowInput;
        update: (
            proxy: DataProxy,
            update: {
                data?: SendNowQueuedEmailMutation;
            }
        ) => void;
    }) => Promise<any>;
};

type WithSendNow = WithRetryQueuedEmail & OnSendNowEmailProps;

type OnDeleteSequenceProps = {
    onDeleteSequence: (arg1: {
        payload: EmailSequenceDeleteInput;
        update: (
            proxy: DataProxy,
            update: {
                data?: DeleteEmailSequenceMutation;
            }
        ) => void;
    }) => Promise<any>;
};

type WithDeleteQueuedEmail = WithSendNow & OnDeleteSequenceProps;

type OnDelaySequenceProps = {
    onDelay: (arg1: {
        payload: DelayQueuedEmailInput;
        update: (
            proxy: DataProxy,
            update: {
                data?: DelayQueuedEmailMutation;
            }
        ) => void;
    }) => Promise<any>;
};

type Props = WithDeleteQueuedEmail & OnDelaySequenceProps;

type State = {
    isDeletingEmail: boolean;
};

export class QueuedEmailRowItemComponent extends React.Component<Props, State> {
    constructor() {
        // @ts-expect-error
        super();
        this.state = {
            isDeletingEmail: false,
        };
    }

    render() {
        const {queuedEmail, isExpanded} = this.props;

        const emailListItem = mapEmailQueuedToEmailListItem(queuedEmail);

        return (
            <EmailRow
                key={queuedEmail.id}
                emailListItem={emailListItem}
                isQueued={true}
                isFirstOfDate={this.props.isFirstOfDate}
                sequenceInfo={getSequenceInfo(queuedEmail)}
                // @ts-expect-error
                status={calculateEmailStatus(queuedEmail)}
                isExpanded={isExpanded}
                navToSequenceDetails={this.props.navToSequenceDetails}
                onDeleteConfirm={this.handleDeleteConfirm}
                onDeleteCancel={this.handleDeleteCancel}
                // @ts-expect-error
                actions={getActionMenuOptions(queuedEmail, {
                    onEmailRetried: this.props.onEmailRetried,
                    onEmailSendNow: this.props.onEmailSendNow,
                    onEmailDelayed: this.props.onEmailDelayed,
                    onRetry: this.props.onRetry,
                    onSendNow: this.props.onSendNow,
                    onDelay: this.props.onDelay,
                    onNavToSequenceDetails: this.props.navToSequenceDetails,
                    onCancel: () => this.setState({isDeletingEmail: true}),
                    onEmailEdit: this.props.onEmailEdit,
                })}
                isDeletingEmail={this.state.isDeletingEmail}
                deleteQuestion='Are you sure you want to cancel the entire sequence?'
            />
        );
    }

    handleDeleteCancel = (e: React.SyntheticEvent<any>) => {
        e.stopPropagation();
        this.setState({isDeletingEmail: false});
    };

    handleDeleteConfirm = (e: React.SyntheticEvent<any>) => {
        e.stopPropagation();
        this.setState({isDeletingEmail: false});

        if (this.props.queuedEmail.emailSequence && this.props.onSequenceDeleted) {
            return this.props.onDeleteSequence({
                payload: {
                    emailSequence: {
                        id: this.props.queuedEmail.emailSequence.id,
                    },
                },
                update: this.props.onSequenceDeleted,
            });
        } else {
            // TODO: This is possible but not implemented yet, will
            // need to implement a mutation for this scenario
            return Promise.reject('Something went wrong');
        }
    };
}

function getDetailFromSendProblems(problems: QueuedEmailFragment['sendProblems']) {
    // Functionally this shouldn't happen as we check for problems before we get here
    if (!problems || problems.length < 1) return '';

    if (problems.find((problem) => problem === 'NO_FOLLOWUP_CAPABILITY')) {
        return 'Sending followup emails requires a Nutshell Pro plan. Please visit the billing page to upgrade your account';
    }

    if (problems.find((problem) => problem === 'SENDER_ADDRESS_DISABLED')) {
        return 'There’s a problem with your email account. Please check your email settings';
    }

    if (problems.find((problem) => problem === 'NO_ENTITIES')) {
        return 'The recipient of this email is deleted from Nutshell';
    }

    if (problems.find((problem) => problem === 'PREVIOUS_EMAIL_NOT_SENT')) {
        return 'The previous email wasn’t sent';
    }

    return 'Something went wrong sending this email. Please contact support for assistance';
}

function calculateEmailStatus(email: QueuedEmailFragment) {
    if (email.status === 'POSTPONED') {
        return {
            level: 'alert',
            text: 'Did not send',
            detail: getDetailFromSendProblems(email.sendProblems),
        };
    }

    if (email.status === 'PAUSED') {
        return {
            level: 'warning',
            text: 'Paused',
            detail: 'This email will not send until you turn back on automated email sending',
        };
    }

    if (email.status === 'FAILED') {
        return {
            level: 'alert',
            text: 'Did not send',
            detail: 'We were unable to send the email',
        };
    }

    if (email.status === 'WONT_SEND') {
        return {
            level: 'warning',
            text: 'Will not send',
            detail: getDetailFromSendProblems(email.sendProblems),
        };
    }

    if (email.status === 'DELIVERING') {
        return {
            level: 'info',
            text: 'Sending',
        };
    }

    if (email.status === 'DELIVERED') {
        return {
            level: 'success',
            text: 'Sent',
        };
    }

    return {
        level: 'info',
        text: 'Scheduled',
    };
}

class RetryMutation extends Mutation<RetryQueuedEmailMutation, RetryQueuedEmailMutationVariables> {}
class SendNowMutation extends Mutation<
    SendNowQueuedEmailMutation,
    SendNowQueuedEmailMutationVariables
> {}
class DeleteMutation extends Mutation<
    DeleteEmailSequenceMutation,
    DeleteEmailSequenceMutationVariables
> {}
class DelayMutation extends Mutation<DelayQueuedEmailMutation, DelayQueuedEmailMutationVariables> {}

export const QueuedEmailRowItem = (props: OwnProps) => (
    <RetryMutation mutation={RETRY_QUEUED_EMAIL}>
        {(retry) => (
            <SendNowMutation mutation={SEND_NOW_QUEUED_EMAIL} refetchQueries={['GetEmailStats']}>
                {(sendNow) => (
                    <DeleteMutation
                        mutation={DELETE_EMAIL_SEQUENCE}
                        refetchQueries={['GetEmailStats']}
                    >
                        {(deleteSequence) => (
                            <DelayMutation mutation={DELAY_QUEUED_EMAIL}>
                                {(delay) => (
                                    <QueuedEmailRowItemComponent
                                        {...props}
                                        onRetry={({payload, update}) => {
                                            if (props.onEmailUpdate) {
                                                props.onEmailUpdate();
                                            }

                                            return retry({variables: {payload}, update});
                                        }}
                                        onSendNow={({payload, update}) => {
                                            if (props.onEmailUpdate) {
                                                props.onEmailUpdate();
                                            }

                                            return sendNow({variables: {payload}, update});
                                        }}
                                        onDeleteSequence={({payload, update}) => {
                                            if (props.onEmailUpdate) {
                                                props.onEmailUpdate();
                                            }

                                            return deleteSequence({variables: {payload}, update});
                                        }}
                                        onDelay={({payload, update}) => {
                                            if (props.onEmailUpdate) {
                                                props.onEmailUpdate();
                                            }

                                            return delay({variables: {payload}, update});
                                        }}
                                    />
                                )}
                            </DelayMutation>
                        )}
                    </DeleteMutation>
                )}
            </SendNowMutation>
        )}
    </RetryMutation>
);
